// TutoModal — Composant générique pour afficher un tuto de la base de connaissance
import { useState, useEffect } from "react";
import { X, Clock, ArrowRight, AlertTriangle, Lightbulb, BookOpen } from "lucide-react";
import { API, getAuthHeaders } from "../pages/metier/dossier/helpers";

export function TutoModal({ slug, isOpen, onClose, tenantId }) {
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen || !slug) return;
    setLoading(true);
    setError(null);
    const headers = getAuthHeaders(tenantId);
    fetch(`${API}/api/kb/article/${slug}`, { headers, credentials: "include" })
      .then((r) => {
        if (!r.ok) throw new Error("Article non trouvé");
        return r.json();
      })
      .then((data) => setArticle(data))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [slug, isOpen, tenantId]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="tuto-modal"
    >
      <div
        className="relative w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-xl shadow-2xl"
        style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-zinc-500/20 transition-colors z-10"
          data-testid="tuto-modal-close"
        >
          <X className="w-4 h-4" style={{ color: "var(--oh-text-muted)" }} />
        </button>

        {loading && (
          <div className="p-12 text-center" style={{ color: "var(--oh-text-muted)" }}>
            <div className="animate-spin w-6 h-6 border-2 border-current border-t-transparent rounded-full mx-auto mb-3" />
            Chargement...
          </div>
        )}

        {error && (
          <div className="p-12 text-center text-[var(--oh-error-text)]">
            <AlertTriangle className="w-6 h-6 mx-auto mb-2" />
            {error}
          </div>
        )}

        {article && !loading && (
          <div className="p-6">
            {/* Header */}
            <div className="flex items-start gap-3 mb-4 pr-8">
              <div
                className="flex items-center justify-center w-10 h-10 rounded-lg flex-shrink-0"
                style={{ background: "var(--oh-accent, var(--oh-stat-blue-text))", opacity: 0.15 }}
              >
                <BookOpen className="w-5 h-5" style={{ color: "var(--oh-accent, var(--oh-stat-blue-text))" }} />
              </div>
              <div>
                <h2 className="text-lg font-medium" style={{ color: "var(--oh-text)" }} data-testid="tuto-modal-title">
                  {article.title}
                </h2>
                <div className="flex items-center gap-3 mt-1">
                  {article.duree && (
                    <span className="flex items-center gap-1 text-xs" style={{ color: "var(--oh-text-muted)" }}>
                      <Clock className="w-3 h-3" /> {article.duree}
                    </span>
                  )}
                  {article.target_profile && (
                    <span
                      className="text-[10px] px-2 py-0.5 rounded-full font-medium uppercase tracking-wider"
                      style={{ background: "#8b5cf620", color: "#a78bfa" }}
                    >
                      {article.target_profile}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Equivalent Qhare */}
            {article.equivalent_qhare && (
              <div
                className="rounded-lg px-3 py-2 mb-4 text-xs flex items-center gap-2"
                style={{ background: "var(--oh-bg-surface)", border: "1px solid var(--oh-border)" }}
              >
                <span style={{ color: "var(--oh-text-muted)" }}>Avant (Qhare/Sheet) :</span>
                <span style={{ color: "var(--oh-text)" }}>{article.equivalent_qhare}</span>
              </div>
            )}

            {/* Etapes */}
            {article.etapes && article.etapes.length > 0 && (
              <div className="mb-4" data-testid="tuto-etapes">
                <h3 className="text-xs font-medium uppercase tracking-wider mb-3" style={{ color: "var(--oh-text-muted)" }}>
                  Étapes
                </h3>
                <ol className="space-y-2">
                  {article.etapes.map((etape, idx) => (
                    <li key={idx} className="flex gap-3 items-start">
                      <span
                        className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium"
                        style={{
                          background: "var(--oh-accent, var(--oh-stat-blue-text))",
                          color: "#fff",
                          opacity: 0.9,
                        }}
                      >
                        {idx + 1}
                      </span>
                      <p className="text-sm pt-0.5" style={{ color: "var(--oh-text)" }}>{etape}</p>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {/* Tables */}
            {article.tables && article.tables.length > 0 && article.tables.map((table, tIdx) => (
              <div key={tIdx} className="mb-4 overflow-x-auto" data-testid={`tuto-table-${tIdx}`}>
                <table className="w-full text-sm" style={{ borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      {table.headers.map((h, i) => (
                        <th
                          key={i}
                          className="text-left px-3 py-2 text-xs font-medium uppercase tracking-wider"
                          style={{
                            color: "var(--oh-text-muted)",
                            borderBottom: "1px solid var(--oh-border)",
                            background: "var(--oh-bg-surface)",
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {table.rows.map((row, rIdx) => (
                      <tr key={rIdx}>
                        {row.map((cell, cIdx) => (
                          <td
                            key={cIdx}
                            className="px-3 py-2"
                            style={{
                              color: "var(--oh-text)",
                              borderBottom: "1px solid var(--oh-border)",
                            }}
                          >
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}

            {/* Attention */}
            {article.attention && (
              <div
                className="rounded-lg px-4 py-3 mb-3 flex gap-3 items-start"
                style={{ background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.2)" }}
                data-testid="tuto-attention"
              >
                <AlertTriangle className="w-4 h-4 text-[var(--oh-info-text)] flex-shrink-0 mt-0.5" />
                <p className="text-sm text-[var(--oh-info-text)]">{article.attention}</p>
              </div>
            )}

            {/* Astuce */}
            {article.astuce && (
              <div
                className="rounded-lg px-4 py-3 mb-3 flex gap-3 items-start"
                style={{ background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.2)" }}
                data-testid="tuto-astuce"
              >
                <Lightbulb className="w-4 h-4 text-[var(--oh-success-text)] flex-shrink-0 mt-0.5" />
                <p className="text-sm text-[var(--oh-success-text)]">{article.astuce}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
